/**
 * Dropdown Component
 *
 * Accessible dropdown/popover with automatic close behavior.
 *
 * @component dropdown-component
 * @extends Component
 *
 * @description
 * Provides dropdown functionality with accessibility features and smart close behavior.
 * Used for desktop filter dropdowns, sort menus, and other popover content.
 *
 * @behavior
 * - Toggles visibility via ARIA attributes (ariaExpanded, ariaHidden)
 * - Closes on outside click
 * - Closes on Escape key
 * - Optional: Closes on scroll (when dropdown is near top of viewport)
 *
 * @scroll_close_behavior
 * When `close-on-scroll="true"` attribute is set:
 * - Tracks when dropdown is near top of viewport (within 100px)
 * - Records scroll position when dropdown enters "near-top zone"
 * - Closes dropdown if user scrolls more than 50px while in near-top zone
 * - Does NOT close during AJAX loading (checks section.ariaBusy)
 * - Throttled to 100ms for performance
 *
 * **Why this pattern?**
 * Desktop filter dropdowns use sticky positioning. When user scrolls down with
 * dropdown open, it becomes sticky at top. This scroll-close behavior prevents
 * the dropdown from obscuring content while sticky.
 *
 * @refs
 * @ref {HTMLElement} trigger - Element that opens/closes dropdown (required)
 * @ref {HTMLElement} content - Dropdown content container (required)
 *
 * @attributes
 * @attr {string} close-on-scroll - Set to "true" to enable scroll-close behavior
 *
 * @fires dropdown:toggle - Custom event to control dropdown state
 * @listens dropdown:toggle - Responds to toggle events from other components
 *
 * @example
 * ```liquid
 * <dropdown-component close-on-scroll="true">
 *   <button ref="trigger">Open Dropdown</button>
 *   <div ref="content" aria-hidden="true">
 *     <!-- Dropdown content -->
 *   </div>
 * </dropdown-component>
 * ```
 *
 * @example
 * Programmatically controlling dropdown:
 * ```javascript
 * const dropdown = document.querySelector('dropdown-component');
 * dropdown.dispatchEvent(new CustomEvent('dropdown:toggle', {
 *   detail: { el: dropdown, open: false },
 *   bubbles: true
 * }));
 * ```
 *
 * @accessibility
 * - Sets aria-expanded on component
 * - Sets aria-hidden on content
 * - Returns focus to trigger on Escape
 * - Keyboard navigable
 *
 * @dependencies
 * - @theme/scripts/component - Base Component class
 * - @theme/scripts/utils - throttle utility
 */

import { Component } from '@theme/scripts/component';
import { throttle } from '@theme/scripts/utils';

class DropdownComponent extends Component {
  requiredRefs = ['trigger', 'content'];

  #open = false;
  #clickHandler = null;
  #outsideClickHandler = null;
  #keyHandler = null;
  #toggleHandler = null;
  #scrollHandler = null;
  #initialScrollPosition = 0;
  #scrollPositionWhenNearTop = null;

  connectedCallback() {
    super.connectedCallback();

    // Store section reference
    this.section = this.closest('.shopify-section');

    this.ariaExpanded = false;
    this.ariaHasPopout = true;

    this.classList.add('group/dropdown');

    this.#open = this.refs.content.ariaHidden !== 'true';

    this.#toggleHandler = (e) => this.handleToggleEvent(e);
    this.addEventListener('dropdown:toggle', this.#toggleHandler, { capture: true });

    this.#clickHandler = (e) => this.handleClick(e);
    this.refs.trigger.addEventListener('click', this.#clickHandler);

    if (this.#open) {
      this.open();
    }
  }

  handleClickOutside(e) {
    if (this.refs.content.contains(e.target)) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent('dropdown:toggle', {
        detail: {
          el: this,
          open: false,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  handleEscape(e) {
    if (e.key === 'Escape') {
      this.dispatchEvent(
        new CustomEvent('dropdown:toggle', {
          detail: {
            el: this,
            open: false,
          },
          bubbles: true,
          composed: true,
        })
      );

      this.refs.trigger.focus();
    }
  }

  open() {
    this.refs.content.ariaHidden = false;
    this.ariaExpanded = true;
    this.#open = true;

    // Track initial scroll position
    this.#initialScrollPosition = window.scrollY;
    this.#scrollPositionWhenNearTop = null; // Reset tracking

    // Check close-on-scroll attribute (opt-in only)
    const closeOnScrollAttr = this.getAttribute('close-on-scroll');
    const shouldAddScrollListener = closeOnScrollAttr === 'true';

    window.requestAnimationFrame(() => {
      this.#outsideClickHandler = (e) => this.handleClickOutside(e);
      this.#keyHandler = (e) => this.handleEscape(e);

      document.addEventListener('click', this.#outsideClickHandler);
      document.addEventListener('keydown', this.#keyHandler);

      // Add scroll listener if explicitly enabled
      if (shouldAddScrollListener) {
        this.#scrollHandler = throttle(() => {
          // Don't close dropdown during section loading
          if (this.section?.ariaBusy === 'true') {
            return;
          }

          const dropdownRect = this.getBoundingClientRect();
          const isNearTop = dropdownRect.top <= 100;

          if (isNearTop) {
            // If we just entered the near-top zone, record this position
            if (this.#scrollPositionWhenNearTop === null) {
              this.#scrollPositionWhenNearTop = window.scrollY;
            }

            // Calculate delta from when we entered near-top zone
            const scrollDelta = Math.abs(window.scrollY - this.#scrollPositionWhenNearTop);

            if (scrollDelta > 50) {
              // Close dropdown via event system
              this.dispatchEvent(
                new CustomEvent('dropdown:toggle', {
                  detail: {
                    el: this,
                    open: false,
                  },
                  bubbles: true,
                  composed: true,
                })
              );
            }
          } else {
            // Reset tracking when leaving near-top zone
            this.#scrollPositionWhenNearTop = null;
          }
        }, 100); // Throttle to 100ms

        window.addEventListener('scroll', this.#scrollHandler, { passive: true });
      }
    });
  }

  close() {
    this.refs.content.ariaHidden = true;
    this.ariaExpanded = false;
    this.#open = false;

    document.removeEventListener('click', this.#outsideClickHandler);
    document.removeEventListener('keydown', this.#keyHandler);

    // Remove scroll listener
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }

    // Reset scroll position tracking
    this.#scrollPositionWhenNearTop = null;

    this.#outsideClickHandler = null;
    this.#keyHandler = null;
  }

  handleToggleEvent(e) {
    const shouldOpen = e.detail.el === this && e.detail.open;

    if (shouldOpen && this.#open === true) {
      return;
    }

    if (shouldOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  handleClick(e) {
    e.preventDefault();

    this.dispatchEvent(
      new CustomEvent('dropdown:toggle', {
        detail: {
          el: this,
          open: !this.#open,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Ensure dropdown is closed and listeners removed
    this.close();

    // Explicit cleanup for scroll handler
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
    }

    this.removeEventListener('dropdown:toggle', this.#toggleHandler, { capture: true });
    this.refs.trigger.removeEventListener('click', this.#clickHandler);
  }
}

if (!customElements.get('dropdown-component')) {
  customElements.define('dropdown-component', DropdownComponent);
}
